export interface PartMetadata {
  seq: number;
  independent?: boolean;
  gopStartSeq?: number;
}

/** Delivery v2 never infers independence from an absent flag or from arrival order. */
export function validPartMetadata(value: PartMetadata): boolean {
  return Number.isSafeInteger(value.seq) && value.seq >= 0 &&
    typeof value.independent === 'boolean' && Number.isSafeInteger(value.gopStartSeq) &&
    value.gopStartSeq! >= 0 && value.gopStartSeq! <= value.seq &&
    (value.independent ? value.gopStartSeq === value.seq : value.gopStartSeq! < value.seq) &&
    value.seq - value.gopStartSeq! <= 8;
}

/** The newest independent, contiguous run ending at the requested part, or no playable run. */
export function playablePartTail<T extends PartMetadata>(entries: readonly T[], targetSeq?: number): T[] {
  const sorted = entries.filter(p => targetSeq === undefined || p.seq <= targetSeq).slice().sort((a, b) => a.seq - b.seq);
  if (!sorted.length || (targetSeq !== undefined && sorted[sorted.length - 1]!.seq !== targetSeq)) return [];
  let from = sorted.length - 1;
  while (from >= 0 && sorted[from]!.independent !== true) from--;
  if (from < 0) return [];
  const run = sorted.slice(from), first = run[0]!;
  if (run.some((part, i) => !validPartMetadata(part) || part.seq !== first.seq + i || part.gopStartSeq !== first.seq)) return [];
  return run;
}

/** Bounds both media coverage and memory represented by a live catalogue. Mutates oldest first. */
export function trimMediaWindow<T extends { durationMs: number; byteLength: number; capturedAt?: number }>(
  entries: T[], limits: { maxDurationMs: number; maxBytes: number; maxEntries: number },
): void {
  let duration = entries.reduce((n, p) => n + p.durationMs, 0);
  let bytes = entries.reduce((n, p) => n + p.byteLength, 0);
  const last = entries[entries.length - 1];
  while (entries.length) {
    const first = entries[0]!;
    const span = last?.capturedAt !== undefined && first.capturedAt !== undefined
      ? last.capturedAt + last.durationMs - first.capturedAt : duration;
    if (duration <= limits.maxDurationMs && span <= limits.maxDurationMs && bytes <= limits.maxBytes && entries.length <= limits.maxEntries) break;
    entries.shift(); duration -= first.durationMs; bytes -= first.byteLength;
  }
}
